import { Injectable } from '@angular/core';
import { Polygon } from 'esri/geometry';

import { BufferConfig, BufferOptions } from '../components/buffer/buffer.config';
import { ESRI } from '../esri-loader-util';
import { AreaUnits } from '../selectionOptions';
import { FillStyle, IBuffer, IMapcoreGraphicsLayer, LineStyle, MapCoreLayer } from '../types';

import { LayerService } from './layer.service';

@Injectable({
    providedIn: 'root'
})
export class BufferService {
    private readonly bufferConfig: BufferConfig;

    public bufferFeatureLimit = 2000;
    private readonly numberAreaDecimals = 5;

    constructor(
        private readonly layerService: LayerService
    ) {
        this.bufferConfig = new BufferConfig();
    }

    getDefaultBufferOptions() {
        return this.bufferConfig.defaultBufferOptions;
    }

    /**
     * Create geodesic buffer polygons client-side around the input layer's geometries.
     * Multiple buffers are returned for each layer if multiple distance values are provided.
     */
    public async getLayerBuffers(layers: MapCoreLayer | MapCoreLayer[], bufferOptions: BufferOptions): Promise<[{ layerId: string; featureCount: number; buffers: IBuffer[] }]> {
        let results = null;
        if (layers) {
            results = [];
            layers = Array.isArray(layers) ? layers : [layers];
            for (const layer of layers) {
                const g = await this.layerService.getLayerGeometry(layer, this.bufferFeatureLimit + 1);
                const geometries = Array.isArray(g) ? g : [g];
                let buffers = [];
                if (geometries.length <= this.bufferFeatureLimit) {
                    buffers = await this.getGeometryBuffers(geometries, bufferOptions);
                    for (const buffer of buffers) {
                        buffer.layer = layer;
                        buffer.title = this.getBufferTitle(buffer);
                    }
                }
                results.push({
                    layerId: layer.id,
                    featureCount: geometries.length,
                    buffers
                });
            }
        }
        return results;
    }

    /**
     * Create geodesic buffer polygons client-side around the input geometries.
     * Multiple buffers are returned for each layer if multiple distance values are provided.
     */
    public async getGeometryBuffers(geometry: __esri.Geometry | __esri.Geometry[], bufferOptions: BufferOptions): Promise<IBuffer[]> {
        const Graphic = await ESRI.Graphic;
        const buffers: IBuffer[] = [];
        const symbol = await this.getSymbolProperties(bufferOptions);
        const distances = Array.isArray(bufferOptions.distance) ? bufferOptions.distance : [bufferOptions.distance];
        for (const distance of distances) {
            const result = await this.applyBuffer(geometry, bufferOptions, distance);
            const polygons = Array.isArray(result) ? result : [result];
            for (const polygon of polygons) {
                const graphic = new Graphic({ symbol, geometry: polygon });
                const buffer: IBuffer = { title: '', distance, distanceUnit: bufferOptions.distanceUnit, graphic };
                buffer.title = this.getBufferTitle(buffer);
                if (bufferOptions.areaUnit) {
                    buffer.areaUnit = bufferOptions.areaUnit;
                    buffer.area = await this.calculateArea(polygon, bufferOptions.areaUnit);
                }
                buffers.push(buffer);
            }
        }
        return buffers;
    }

    async applyBuffer(geometry: __esri.Geometry | __esri.Geometry[], bufferOptions: BufferOptions, distance: number): Promise<Polygon | Polygon[]> {
        const geometryEngine = await ESRI.GeometryEngineAsync;
        return geometryEngine.geodesicBuffer(geometry, distance, bufferOptions.distanceUnit, bufferOptions.unionResults);
    }

    async calculateArea(geometry: __esri.Polygon, unit: __esri.ArealUnits | number): Promise<number> {
        const geometryEngine = await ESRI.GeometryEngineAsync;
        return geometryEngine.geodesicArea(geometry, unit);
    }

    async getSymbolProperties(bufferOptions: BufferOptions): Promise<__esri.SimpleFillSymbolProperties> {
        const [Color] = await ESRI.loadModules('esri/Color');
        const color = new Color(bufferOptions.fillColor);
        if (color.a < 1) {
            bufferOptions.opacity = 1;
        }
        const outlineColor = new Color(bufferOptions.outlineColor);
        return {
            type: 'simple-fill',
            color,
            style: <FillStyle>bufferOptions.fillType,
            outline: {
                color: outlineColor,
                style: <LineStyle>bufferOptions.outlineType,
                width: bufferOptions.lineWidth
            }
        };
    }

    private getBufferTitle(buffer: IBuffer) {
        let title = 'Geometry';
        if (buffer.layer) {
            if (buffer.layer.mapcoreProps && buffer.layer.mapcoreProps.name) {
                title = buffer.layer.mapcoreProps.name;
            } else if ((<IMapcoreGraphicsLayer>buffer.layer).graphics) {
                title = (<IMapcoreGraphicsLayer>buffer.layer)[0].geometry.type;
            }
        }
        title += ` - Buffer (${buffer.distance} ${buffer.distanceUnit})`;
        if (buffer.area) {
            const unit = AreaUnits.find(u => u.value === buffer.areaUnit);
            title += ` (${buffer.area.toFixed(this.numberAreaDecimals)} ${unit.name})`;
        }
        return title[0].toUpperCase() + title.slice(1);
    }

    public async addBufferToMap(buffers: IBuffer | IBuffer[], bufferOptions: BufferOptions): Promise<IMapcoreGraphicsLayer[]> {
        const graphicsLayers = [];
        buffers = Array.isArray(buffers) ? buffers : [buffers];
        for (const buffer of buffers) {
            const graphicsLayer = await this.layerService.addGraphicsLayer({ title: buffer.title, graphicLayer: 'polygon', opacity: bufferOptions.opacity });
            graphicsLayer.add(buffer.graphic);
            graphicsLayers.push(graphicsLayer);
        }
        bufferOptions.opacity = this.getDefaultBufferOptions().opacity;
        return graphicsLayers;
    }

}
